import * as z from 'zod'
import { Theme as EmotionTheme, useTheme as useEmotionTheme } from '@emotion/react'
import styled from '@emotion/styled'
import { zodResolver } from '@hookform/resolvers/zod'
import { useRouter } from 'next/router'
import React from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useEffectOnce, usePrevious, useUpdateEffect } from 'react-use'
import isURL from 'validator/lib/isURL'

import { isEmpty } from '@livechat/data-utils'

import FeatureLink from '../FeatureLink'
import SlideInWrapper from '../SlideInWrapper'
import { usePreviewController } from '../WidgetPreview/previewController'
import Button from 'components/Button'
import HoverableItem from 'components/HoverableItem'
import FieldError from 'components/fields/FieldError'
import Fieldset from 'components/fields/Fieldset'
import Form from 'components/fields/Form'
import FormRow from 'components/fields/FormRow'
import ImageUpload from 'components/fields/ImageUpload'
import Input from 'components/fields/Input'
import Label from 'components/fields/Label'
import Legend from 'components/fields/Legend'
import Select from 'components/fields/Select'
import {
	AlignLeftIcon,
	AlignRightIcon,
	ChevronIcon,
	InfoIcon,
	OffsetSideIcon,
	VariantDarkIcon,
	VariantLightIcon,
} from 'components/icons'
import BackgroundTypeSelection from 'components/theming/BackgroundTypeSelection'
import ExtendedColorsConfiguration from 'components/theming/ExtendedColorsConfiguration'
import PrimaryColorForm from 'components/theming/PrimaryColorForm'
import { EXAMPLE_COLOR_CODES } from 'components/theming/constants'
import IconRadio from 'components/theming/radio/IconRadio'
import PillRadio from 'components/theming/radio/PillRadio'
import { useHeader } from 'hooks/useHeader'
import { useInstallations } from 'hooks/useInstallations'
import { useIsOnboarding } from 'hooks/useIsOnboarding'
import { useOrganization } from 'hooks/useOrganization'
import { useTemplate } from 'hooks/useTemplate'
import { useTheme } from 'hooks/useTheme'
import { useToast } from 'hooks/useToast'
import { useTracking } from 'hooks/useTracking'
import { useUser } from 'hooks/useUser'
import { getWCAGTextColor } from 'lib/WCAGContrast'
import { updateHeader, updateOrganization, updateTemplate, updateTheme } from 'lib/api'
import { clamp } from 'lib/clamp'
import { LANGUAGE_CODES, LANGUAGE_CODES_TO_NAMES_MAP, TEMP_ENTITY_PREFIX } from 'lib/constants'
import { ensureProtocol, isValidHexColor, scrollToPreviewTop } from 'lib/helpers'
import { Store, useAppStore } from 'lib/store'
import { EVENT_NAMES } from 'lib/tracking'
import { Header } from 'types/header'
import { LanguageCode } from 'types/localization'
import { ExtendedCustomizableColors, NewTheme, Theme } from 'types/theme'

const OffsetLabel = styled.span`
	font-weight: normal;
	display: flex;
	align-items: center;

	span {
		margin-left: ${({ theme }) => theme.spaces.space3};
	}
`

const ActionContainer = styled.div`
	width: 300px;
	display: flex;
	flex-direction: row;
	justify-content: space-between;
	align-items: baseline;
	gap: ${({ theme }) => theme.spaces.space7};
`

const OffsetBottomIcon = styled(OffsetSideIcon)`
	rotate: -90deg;
`

const OffsetRow = styled(FormRow)`
	position: relative;
	padding-bottom: 6px;
`

const LabelWrapper = styled.div`
	display: flex;
	align-items: center;
	gap: ${({ theme }) => theme.spaces.space3};
	padding-right: ${({ theme }) => theme.spaces.space3};
`

const StyledFieldset = styled(Fieldset)`
	max-width: 380px;
`

const ThemeVariantsFormRow = styled(FormRow)`
	gap: ${({ theme }) => theme.spaces.space6};
	width: calc(100% - ${({ theme }) => theme.spaces.space6});
`

const StyledSelect = styled(Select)`
	font-size: ${({ theme }) => theme.fontSizes.size16};
	padding: ${({ theme }) => theme.spaces.space5} ${({ theme }) => theme.spaces.space6};
`

const AlignmentPillRadioInner = styled.span`
	padding: ${({ theme }) => theme.spaces.space5};
	display: flex;
	align-items: center;
`

const HexColorSchema = z.string().regex(/(^#[0-9A-F]{6}$)|(^#[0-9A-F]{3}$)/i)

const FormSchema = z
	.object({
		logo: z.union([z.literal(''), z.string().refine(isURL, 'Invalid URL format')]),
		headerText: z
			.string()
			.min(1)
			.max(50, 'Cannot be longer than 50 characters')
			.regex(/.*\S.*/, "Field can't be empty"),
		themeVariant: z.union([z.literal('light'), z.literal('dark')]),
		primaryColor: HexColorSchema,
		screenPosition: z.union([z.literal('left'), z.literal('right')]),
		offsetX: z.number({ invalid_type_error: 'Provide a number' }).min(0).max(100),
		offsetY: z.number({ invalid_type_error: 'Provide a number' }).min(0).max(100),
		language: z.enum(LANGUAGE_CODES),
		visitorMessageColorText: HexColorSchema,
		visitorMessageColorBackground: HexColorSchema,
		agentMessageColorText: HexColorSchema,
		agentMessageColorBackground: HexColorSchema,
		minimizedColorIcon: HexColorSchema,
		minimizedColorBackground: HexColorSchema,
		headerBackgroundImage: z.union([z.literal(''), z.string().refine(isURL, 'Invalid URL format')]),
		headerBackgroundType: z.union([z.literal('solid'), z.literal('gradient'), z.literal('image')]),
	})
	.superRefine((data, ctx) => {
		if (data.headerBackgroundType === 'image' && !data.headerBackgroundImage) {
			ctx.addIssue({
				path: ['headerBackgroundType'],
				code: 'invalid_intersection_types',
				message: 'Choose a background or upload your own',
			})
		}
	})

export type FormValues = z.infer<typeof FormSchema>

function syncStoreWithForm(
	store: Store,
	formValues: Partial<FormValues>,
	emotionTheme: EmotionTheme,
	serverTheme: Theme,
) {
	const variant = formValues.themeVariant ?? 'light'
	const primaryColor = formValues.primaryColor ?? EXAMPLE_COLOR_CODES[variant][0].value
	const defaultAdvancedColors = getDefaultAdvancedColors(emotionTheme, primaryColor, variant)

	store.dispatch({
		type: 'theme/set',
		payload: {
			...serverTheme,
			id: `${TEMP_ENTITY_PREFIX}-theme`,
			variant,
			primaryColor,
			screenPosition: formValues.screenPosition ?? 'right',
			offsetX: formValues.offsetX ?? 0,
			offsetY: formValues.offsetY ?? 0,
			visitorMessageColorText: formValues.visitorMessageColorText ?? defaultAdvancedColors.visitorMessageColorText,
			visitorMessageColorBackground:
				formValues.visitorMessageColorBackground ?? defaultAdvancedColors.visitorMessageColorBackground,
			agentMessageColorText: formValues.agentMessageColorText ?? defaultAdvancedColors.agentMessageColorText,
			agentMessageColorBackground:
				formValues.agentMessageColorBackground ?? defaultAdvancedColors.agentMessageColorBackground,
			minimizedColorIcon: formValues.minimizedColorIcon ?? defaultAdvancedColors.minimizedColorIcon,
			minimizedColorBackground: formValues.minimizedColorBackground ?? defaultAdvancedColors.minimizedColorBackground,
		},
	})

	store.dispatch({
		type: 'header/set',
		payload: {
			id: `${TEMP_ENTITY_PREFIX}-header`,
			logo: formValues.logo && isURL(formValues.logo) ? ensureProtocol(formValues.logo) : '',
			text: formValues.headerText ?? '',
			backgroundImage:
				formValues.headerBackgroundImage && isURL(formValues.headerBackgroundImage)
					? ensureProtocol(formValues.headerBackgroundImage)
					: '',
			backgroundType: formValues.headerBackgroundType ?? 'gradient',
		},
	})
}

type Props = {
	id: string
	onSubmit?: VoidFunction
	onIsValidChanged?(isValid: boolean, id: string): void
	onIsDirtyChanged?(isDirty: boolean, id: string): void
	onIsSubmittingChanged?(isSubmitting: boolean, id: string): void
	onHasErrorsChanged?(hasErrors: boolean, id: string): void
}

const getDefaultAdvancedColors = (
	theme: EmotionTheme,
	primaryColor: string,
	variant: 'dark' | 'light',
): { [K in keyof ExtendedCustomizableColors]: Exclude<ExtendedCustomizableColors[K], null> } => {
	const isPrimaryColorValid = isValidHexColor(primaryColor)
	const ctaContrastingTextColor = isPrimaryColorValid
		? getWCAGTextColor(theme.colorPalette.grayscale[0], theme.colorPalette.grayscale[900], primaryColor)
		: theme.colorPalette.grayscale[0]

	return {
		visitorMessageColorText: ctaContrastingTextColor,
		visitorMessageColorBackground: primaryColor,
		agentMessageColorText: theme.colors[variant].primaryTextColor,
		agentMessageColorBackground: theme.colors[variant].surface,
		minimizedColorIcon: ctaContrastingTextColor,
		minimizedColorBackground: primaryColor,
	}
}

function withDefaulValues(FormComponent: typeof ThemeForm) {
	const WithDefaultValues = React.forwardRef<HTMLFormElement, Props>((props, ref) => {
		const { header: serverHeader } = useHeader()
		const { theme: serverTheme, isThemeLoading } = useTheme()
		const emotionTheme = useEmotionTheme()
		const { template, isTemplateLoading } = useTemplate()

		if (isThemeLoading || isTemplateLoading || !serverHeader || !serverTheme || !template) {
			return null
		}

		const parseDataToFormValues = (header: Header, theme: NewTheme, language: LanguageCode): FormValues => {
			const defaultAdvancedColors = getDefaultAdvancedColors(emotionTheme, theme.primaryColor, theme.variant)

			return {
				logo: header.logo && isURL(header.logo) ? ensureProtocol(header.logo) : '',
				headerText: header.text,
				themeVariant: theme.variant,
				primaryColor: theme.primaryColor,
				screenPosition: theme.screenPosition,
				offsetX: theme.offsetX,
				offsetY: theme.offsetY,
				language,
				visitorMessageColorText: theme.visitorMessageColorText ?? defaultAdvancedColors.visitorMessageColorText,
				visitorMessageColorBackground:
					theme.visitorMessageColorBackground ?? defaultAdvancedColors.visitorMessageColorBackground,
				agentMessageColorText: theme.agentMessageColorText ?? defaultAdvancedColors.agentMessageColorText,
				agentMessageColorBackground:
					theme.agentMessageColorBackground ?? defaultAdvancedColors.agentMessageColorBackground,
				minimizedColorIcon: theme.minimizedColorIcon ?? defaultAdvancedColors.minimizedColorIcon,
				minimizedColorBackground: theme.minimizedColorBackground ?? defaultAdvancedColors.minimizedColorBackground,
				headerBackgroundImage:
					header.backgroundImage && isURL(header.backgroundImage) ? ensureProtocol(header.backgroundImage) : '',
				headerBackgroundType: header.backgroundType ?? 'gradient',
			}
		}

		const defaultValues = parseDataToFormValues(serverHeader, serverTheme, template.language)

		return <FormComponent {...props} ref={ref} defaultValues={defaultValues} serverTheme={serverTheme} />
	})

	WithDefaultValues.displayName = `WithDefaultValues(${FormComponent.displayName})`

	return WithDefaultValues
}

const ThemeForm = React.forwardRef<HTMLFormElement, Props & { defaultValues: FormValues; serverTheme: Theme }>(
	(
		{
			id,
			defaultValues,
			onIsValidChanged,
			onIsDirtyChanged,
			onIsSubmittingChanged,
			onHasErrorsChanged,
			onSubmit,
			serverTheme,
		},
		ref,
	) => {
		const store = useAppStore()
		const track = useTracking()
		const router = useRouter()
		const user = useUser()
		const { mutateHeader } = useHeader()
		const { mutateTheme } = useTheme()
		const emotionTheme = useEmotionTheme()
		const showToast = useToast()
		const previewController = usePreviewController()
		const form = useForm<FormValues>({
			defaultValues,
			mode: 'onTouched',
			resolver: zodResolver(FormSchema),
		})
		const { register, handleSubmit, watch, reset, getValues, formState, setValue } = form
		const { errors, isValid, isSubmitting, isDirty, dirtyFields } = formState
		const shouldToggleAligment = router.route.startsWith('/onboarding/')
		const isOnboarding = useIsOnboarding()

		const [isAlignmentVisible, setIsAlignmentVisible] = React.useState(!shouldToggleAligment)
		const [isExtendedColorConfigurationVisible, setIsExtendedColorConfigurationVisible] = React.useState(false)
		const previousIsAlignmentVisible = usePrevious(isAlignmentVisible)
		const prevIsSubmitting = usePrevious(isSubmitting)
		const { mutateOrganization } = useOrganization()
		const { mutateTemplate, template } = useTemplate()
		const { installations } = useInstallations()
		const hasErrors = !isEmpty(errors)

		const currentLanguage = watch('language')
		const currentPrimaryColor = watch('primaryColor')
		const currentThemeVariant = watch('themeVariant')
		const previousPrimaryColor = usePrevious(currentPrimaryColor)
		const previousThemeVariant = usePrevious(currentThemeVariant)
		const isChatOnlyModeEnabled = Boolean(template?.isChatOnlyModeEnabled)

		React.useEffect(() => {
			store.dispatch({
				type: 'language/set',
				payload: currentLanguage,
			})
		}, [store, currentLanguage])

		useEffectOnce(() => {
			previewController.showView('Homescreen')
			scrollToPreviewTop()
		})

		useEffectOnce(() => {
			syncStoreWithForm(store, getValues(), emotionTheme, serverTheme)
			const { unsubscribe } = watch(() => {
				const offsetX = getValues('offsetX')
				const offsetY = getValues('offsetY')

				syncStoreWithForm(
					store,
					{
						...getValues(),
						offsetX: clamp(offsetX, 0, 100),
						offsetY: clamp(offsetY, 0, 100),
					},
					emotionTheme,
					serverTheme,
				)
			})
			return () => unsubscribe()
		})

		useUpdateEffect(() => {
			if (prevIsSubmitting && !isSubmitting && isValid) {
				reset(defaultValues)
			}
		}, [prevIsSubmitting, isSubmitting, defaultValues, reset, isValid])

		React.useEffect(() => {
			onIsValidChanged?.(isValid, id)
		}, [id, isValid, onIsValidChanged])

		React.useEffect(() => {
			onIsDirtyChanged?.(isDirty, id)
		}, [id, isDirty, onIsDirtyChanged])

		React.useEffect(() => {
			onIsSubmittingChanged?.(isSubmitting, id)
		}, [id, isSubmitting, onIsSubmittingChanged])

		React.useEffect(() => {
			onHasErrorsChanged?.(hasErrors, id)
		}, [id, hasErrors, onHasErrorsChanged])

		const handleReset = (event: React.FormEvent) => {
			event.preventDefault()
			reset(defaultValues)
		}

		React.useEffect(() => {
			if (!isValidHexColor(currentPrimaryColor)) {
				return
			}

			const isThemeVariantChanged = previousThemeVariant && previousThemeVariant !== currentThemeVariant
			const isPrimaryColorChanged = previousPrimaryColor && previousPrimaryColor !== currentPrimaryColor
			if (!isThemeVariantChanged && !isPrimaryColorChanged) {
				return
			}

			requestAnimationFrame(() => {
				const defaultAdvancedColors = getDefaultAdvancedColors(emotionTheme, currentPrimaryColor, currentThemeVariant)
				const colorSettings = [
					'visitorMessageColorText',
					'visitorMessageColorBackground',
					'agentMessageColorText',
					'agentMessageColorBackground',
					'minimizedColorIcon',
					'minimizedColorBackground',
				] as const
				colorSettings.forEach((setting) => {
					setValue(setting, defaultAdvancedColors[setting], {
						shouldDirty: true,
						shouldValidate: true,
					})
				})
			})
		}, [emotionTheme, currentPrimaryColor, currentThemeVariant, setValue, previousPrimaryColor, previousThemeVariant])

		const _onSubmit: Parameters<typeof handleSubmit>['0'] = async () => {
			const { header, theme, language } = store.getState()
			if (!header || !theme || !language) {
				throw new Error('Invalid submission state')
			}

			track(EVENT_NAMES.CUSTOMIZATION_SAVED, {
				logo: header.logo ? 'from URL' : 'unset',
				helloMessageEdited: header.text !== defaultValues.headerText,
				themeVariant: theme.variant,
				primaryColor: theme.primaryColor,
				wasAlignmentChanged:
					defaultValues.offsetX !== theme.offsetX ||
					defaultValues.offsetY !== theme.offsetY ||
					defaultValues.screenPosition !== theme.screenPosition,
				side: theme.screenPosition,
				positionChanged: theme.offsetX > 0 || theme.offsetY > 0,
			})

			try {
				const [updatedHeader, updatedTheme, updatedOrganization, updatedTemplate] = await Promise.all([
					updateHeader({
						...(dirtyFields.logo && { logo: header.logo }),
						...(dirtyFields.headerText && { text: header.text }),
						...(dirtyFields.headerBackgroundImage && { backgroundImage: header.backgroundImage }),
						...(dirtyFields.headerBackgroundType && { backgroundType: header.backgroundType }),
					}),
					updateTheme({
						...(dirtyFields.primaryColor && { primaryColor: theme.primaryColor }),
						...(dirtyFields.themeVariant && { variant: theme.variant }),
						...(dirtyFields.screenPosition && { screenPosition: theme.screenPosition }),
						...(dirtyFields.offsetX && { offsetX: theme.offsetX }),
						...(dirtyFields.offsetY && { offsetY: theme.offsetY }),
						...(dirtyFields.visitorMessageColorText && { visitorMessageColorText: theme.visitorMessageColorText }),
						...(dirtyFields.visitorMessageColorBackground && {
							visitorMessageColorBackground: theme.visitorMessageColorBackground,
						}),
						...(dirtyFields.agentMessageColorText && { agentMessageColorText: theme.agentMessageColorText }),
						...(dirtyFields.agentMessageColorBackground && {
							agentMessageColorBackground: theme.agentMessageColorBackground,
						}),
						...(dirtyFields.minimizedColorIcon && { minimizedColorIcon: theme.minimizedColorIcon }),
						...(dirtyFields.minimizedColorBackground && { minimizedColorBackground: theme.minimizedColorBackground }),
					}),
					updateOrganization({
						...(isOnboarding && {
							onboardingProgress: installations && installations.length > 0 ? 'completed' : 'customize',
						}),
					}),
					updateTemplate(user.organizationId, {
						...(dirtyFields.language && { language }),
					}),
				])

				if (updatedHeader) {
					mutateHeader(updatedHeader, { revalidate: false })
				}
				if (updatedTheme) {
					mutateTheme(updatedTheme, { revalidate: false })
				}
				if (updatedOrganization) {
					mutateOrganization(updatedOrganization, { revalidate: false })
				}
				if (updatedTemplate) {
					mutateTemplate(updatedTemplate, { revalidate: false })
				}

				onSubmit?.()

				if (!isOnboarding) {
					showToast('success')
				}
			} catch (error) {
				showToast('error')
				throw error
			}
		}

		return (
			<FormProvider {...form}>
				<Form id={id} onSubmit={handleSubmit(_onSubmit)} onReset={handleReset} ref={ref}>
					<ImageUpload kind="logo" fieldName="logo" label="Logo" alt="logo" emptyFieldValue="" />
					{!isOnboarding && (
						<StyledFieldset>
							<LabelWrapper>
								<Label htmlFor="language">Choose language</Label>
								<HoverableItem
									position={{ right: 35, bottom: 50 }}
									title="Note that phrases you've already customized won't be automatically translated."
								>
									<InfoIcon />
								</HoverableItem>
							</LabelWrapper>
							<StyledSelect id="language" {...register('language')}>
								{LANGUAGE_CODES.map((languageCode) => (
									<option key={languageCode} value={languageCode}>
										{LANGUAGE_CODES_TO_NAMES_MAP[languageCode]}
									</option>
								))}
							</StyledSelect>
						</StyledFieldset>
					)}
					{!isChatOnlyModeEnabled && (
						<Input
							required
							label="Say hello"
							error={errors.headerText?.message}
							{...register('headerText', { onChange: () => previewController.showView('Homescreen', 'contact-card') })}
							placeholder="Type a welcome message"
						/>
					)}
					<StyledFieldset>
						<Legend>Pick theme and color</Legend>
						<ThemeVariantsFormRow>
							<IconRadio
								label="Light"
								value="light"
								icon={<VariantLightIcon />}
								{...register('themeVariant')}
								aria-label="light theme"
							/>
							<IconRadio
								label="Dark"
								value="dark"
								icon={<VariantDarkIcon />}
								{...register('themeVariant')}
								aria-label="dark theme"
							/>
						</ThemeVariantsFormRow>
						<PrimaryColorForm
							watch={watch}
							register={register}
							onChange={(color) => setValue('primaryColor', color, { shouldValidate: true, shouldDirty: true })}
						/>
						<FormRow>
							<ActionContainer>
								<Legend>More colors settings</Legend>
								<Button
									type="button"
									mediumDesktop
									onClick={() => setIsExtendedColorConfigurationVisible((curr) => !curr)}
									aria-expanded={isExtendedColorConfigurationVisible}
								>
									{isExtendedColorConfigurationVisible ? 'Hide' : 'Show'}
									<ChevronIcon direction={isExtendedColorConfigurationVisible ? 'upwards' : 'downwards'} />
								</Button>
							</ActionContainer>
						</FormRow>
						{isExtendedColorConfigurationVisible && (
							<SlideInWrapper axis="y">
								<ExtendedColorsConfiguration />
							</SlideInWrapper>
						)}
					</StyledFieldset>
					{!isChatOnlyModeEnabled && (
						<StyledFieldset>
							<BackgroundTypeSelection />
						</StyledFieldset>
					)}
					{isOnboarding && (
						<>
							<FormRow>
								<Legend>Align widget</Legend>
								<Button type="button" compact onClick={() => setIsAlignmentVisible(!isAlignmentVisible)}>
									Change position
									<ChevronIcon direction={isAlignmentVisible ? 'upwards' : 'downwards'} />
								</Button>
							</FormRow>
							{isAlignmentVisible && (
								<>
									<Fieldset>
										<ActionContainer>
											<PillRadio value="left" {...register('screenPosition')} aria-label="align to left">
												<AlignmentPillRadioInner>
													<AlignLeftIcon />
													Left
												</AlignmentPillRadioInner>
											</PillRadio>
											<PillRadio value="right" {...register('screenPosition')} aria-label="align to right">
												<AlignmentPillRadioInner>
													<AlignRightIcon />
													Right
												</AlignmentPillRadioInner>
											</PillRadio>
										</ActionContainer>
									</Fieldset>
									<OffsetRow
										ref={(ref) => {
											if (
												ref &&
												shouldToggleAligment &&
												!previousIsAlignmentVisible &&
												isAlignmentVisible &&
												!emotionTheme.isMobile
											) {
												ref.scrollIntoView({ behavior: 'smooth' })
											}
										}}
									>
										<ActionContainer>
											<Input
												type="number"
												inputMode="numeric"
												fullFieldsetWidth
												label={
													<OffsetLabel>
														<OffsetSideIcon />
														<span>Side</span>
													</OffsetLabel>
												}
												min={0}
												max={100}
												{...register('offsetX', {
													min: 0,
													max: 100,
													valueAsNumber: true,
												})}
												aria-label="side offset"
											/>
											<Input
												type="number"
												inputMode="numeric"
												fullFieldsetWidth
												label={
													<OffsetLabel>
														<OffsetBottomIcon />
														<span>Bottom</span>
													</OffsetLabel>
												}
												min={0}
												max={100}
												{...register('offsetY', {
													min: 0,
													max: 100,
													valueAsNumber: true,
												})}
												aria-label="bottom offset"
											/>
											{(errors.offsetY || errors.offsetX) && (
												<FieldError>{(errors.offsetX || errors.offsetY)?.message}</FieldError>
											)}
										</ActionContainer>
									</OffsetRow>
								</>
							)}
						</>
					)}
					{!isOnboarding && (
						<FeatureLink href="/configurator/customize-look/advanced">Widget position and visibility</FeatureLink>
					)}
				</Form>
			</FormProvider>
		)
	},
)

ThemeForm.displayName = 'ThemeForm'

export default withDefaulValues(ThemeForm)
